import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";
import { ShieldCheck, Upload, Camera, CheckCircle2, Clock, AlertCircle, XCircle } from "lucide-react";

const ID_TYPES = [
    { key: "nin", label: "NIN / National ID" },
    { key: "drivers_license", label: "Driver's License" },
    { key: "passport", label: "International Passport" },
    { key: "voter_id", label: "Voter ID" },
    { key: "ghana_card", label: "Ghana Card" },
];

const STATUS_META = {
    pending:   { bg: "bg-slate-100 text-slate-700 border-slate-200", icon: AlertCircle, key: "kyc.status.pending" },
    submitted: { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, key: "kyc.status.submitted" },
    verified:  { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, key: "kyc.status.verified" },
    declined:  { bg: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle, key: "kyc.status.declined" },
};

export default function KycSubmit() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [me, setMe] = useState(null);
    const [form, setForm] = useState({
        national_id: "",
        id_type: "nin",
        selfie_b64: "",
        id_front_b64: "",
        id_back_b64: "",
        address: "",
        dob: "",
    });
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/kyc/me").then((r) => setMe(r.data));
    useEffect(() => { load(); }, []);

    const handleFile = (key) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3_000_000) return toast.error("Image too large (max 3MB)");
        const reader = new FileReader();
        reader.onload = () => setForm((s) => ({ ...s, [key]: reader.result }));
        reader.readAsDataURL(file);
    };

    const submit = async () => {
        if (!form.national_id) return toast.error("Enter your ID number");
        if (!form.selfie_b64 || !form.id_front_b64) return toast.error("Upload selfie and ID front");
        setBusy(true);
        try {
            await api.post("/kyc/submit", form);
            toast.success("KYC submitted! We'll respond within 24 hours.");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    if (!me) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    const meta = STATUS_META[me.kyc_status] || STATUS_META.pending;
    const StatusIcon = meta.icon;
    const canSubmit = me.kyc_status !== "verified" && me.kyc_status !== "submitted";

    return (
        <div data-testid="kyc-page">
            <MobileHeader title={t("kyc.title")} backTo="/consumer/profile" variant="blue">
                <p className="text-xs text-white/80 mt-3">{t("kyc.subtitle")}</p>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                {/* Status card */}
                <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid="kyc-status-card">
                    <div className="flex items-center gap-3">
                        <div className={`size-12 rounded-xl flex items-center justify-center ${meta.bg}`}>
                            <StatusIcon size={22} />
                        </div>
                        <div className="flex-1">
                            <div className="font-display font-semibold text-[#0A1930]">{t("kyc.status.title")}</div>
                            <Badge className={`mt-1 capitalize border ${meta.bg}`}>{t(meta.key)}</Badge>
                        </div>
                    </div>
                    {me.submission?.decision_reason && me.kyc_status === "declined" && (
                        <div className="mt-3 bg-rose-50 rounded-lg p-3 text-xs text-rose-700">
                            <strong>{t("kyc.reviewer_note")}</strong> {me.submission.decision_reason}
                        </div>
                    )}
                </div>

                {me.kyc_status === "verified" && (
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 text-center" data-testid="kyc-verified-banner">
                        <ShieldCheck size={36} className="text-emerald-600 mx-auto" />
                        <div className="font-display font-semibold text-emerald-900 mt-2">{t("kyc.verified.title")}</div>
                        <p className="text-xs text-emerald-700 mt-1">{t("kyc.verified.desc")}</p>
                    </div>
                )}

                {canSubmit && (
                    <>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs">{t("kyc.form.id_type")}</Label>
                                <Select value={form.id_type} onValueChange={(v) => setForm((s) => ({ ...s, id_type: v }))}>
                                    <SelectTrigger className="mt-1 h-11 rounded-xl bg-white" data-testid="id-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ID_TYPES.map((idt) => <SelectItem key={idt.key} value={idt.key}>{idt.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">{t("kyc.form.id_number")}</Label>
                                <Input value={form.national_id} onChange={(e) => setForm((s) => ({ ...s, national_id: e.target.value }))} className="mt-1 h-11 rounded-xl bg-white" data-testid="national-id-input" placeholder={t("kyc.form.id_placeholder")} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-xs">{t("kyc.form.dob")}</Label>
                                    <Input type="date" value={form.dob} onChange={(e) => setForm((s) => ({ ...s, dob: e.target.value }))} className="mt-1 h-11 rounded-xl bg-white" data-testid="dob-input" />
                                </div>
                                <div>
                                    <Label className="text-xs">{t("kyc.form.address")}</Label>
                                    <Input value={form.address} onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))} placeholder={t("kyc.form.address_ph")} className="mt-1 h-11 rounded-xl bg-white" data-testid="address-input" />
                                </div>
                            </div>
                        </div>

                        {/* Image uploads */}
                        <div className="space-y-3">
                            <UploadTile
                                testid="upload-selfie"
                                label={t("kyc.upload.selfie")}
                                hint={t("kyc.upload.selfie_hint")}
                                b64={form.selfie_b64}
                                onFile={handleFile("selfie_b64")}
                                icon={Camera}
                                required
                            />
                            <UploadTile
                                testid="upload-id-front"
                                label={t("kyc.upload.id_front")}
                                hint={t("kyc.upload.id_front_hint")}
                                b64={form.id_front_b64}
                                onFile={handleFile("id_front_b64")}
                                icon={Upload}
                                required
                            />
                            <UploadTile
                                testid="upload-id-back"
                                label={t("kyc.upload.id_back")}
                                hint={t("kyc.upload.id_back_hint")}
                                b64={form.id_back_b64}
                                onFile={handleFile("id_back_b64")}
                                icon={Upload}
                            />
                        </div>

                        <Button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl bg-[#0066FF] hover:bg-[#0052cc] font-semibold" data-testid="kyc-submit-btn">
                            {busy ? t("kyc.submitting") : t("kyc.submit")}
                        </Button>
                    </>
                )}

                {me.kyc_status === "submitted" && (
                    <div className="bg-white rounded-2xl p-5 text-center border border-slate-200/60 product-shadow">
                        <Clock size={28} className="text-amber-500 mx-auto" />
                        <div className="font-display font-semibold text-[#0A1930] mt-2">{t("kyc.submitted.title")}</div>
                        <p className="text-xs text-slate-600 mt-1">{t("kyc.submitted.desc")}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function UploadTile({ testid, label, hint, b64, onFile, icon: Icon, required }) {
    const { t } = useI18n();
    const ref = useRef(null);
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={testid}>
            <div className="flex items-start gap-3">
                <div className="size-12 rounded-xl flex items-center justify-center bg-blue-50 text-[#0066FF] flex-shrink-0">
                    {b64 ? (
                        <img src={b64} alt={label} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                        <Icon size={20} />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm text-[#0A1930]">{label} {required && <span className="text-rose-600">*</span>}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
                    <input ref={ref} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
                    <Button onClick={() => ref.current?.click()} variant="outline" size="sm" className="mt-2 h-8 text-xs rounded-lg" data-testid={`${testid}-btn`}>
                        {b64 ? t("kyc.upload.replace") : t("kyc.upload.upload")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
